import NumberInput from '../../../components/NumberInput'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { supabase, supabaseConfigured } from '../../../lib/supabaseClient'
import { Button } from '../../../components/ui/button'
import { useUnits } from '../../../contexts/UnitsContext'
import { useAuth } from '../../../contexts/AuthContext'
import { cn } from '../../../lib/utils'

const TYPES = ['cut', 'maintain', 'bulk']

export function PhaseForm({ initialValues, onSubmit, onCancel, submitting }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { weightUnit, weightToKg, displayValueWeight } = useUnits()
  const today = new Date().toLocaleDateString('en-CA')

  const [phaseType, setPhaseType] = useState(initialValues?.phase_type || 'cut')
  const [name, setName] = useState(initialValues?.name || '')
  const [startDate, setStartDate] = useState(initialValues?.start_date || today)
  const [endDate, setEndDate] = useState(initialValues?.end_date || '')
  const [startWeight, setStartWeight] = useState(
    initialValues?.start_weight_kg != null
      ? String(displayValueWeight(Number(initialValues.start_weight_kg)).toFixed(1))
      : ''
  )
  const [goalWeight, setGoalWeight] = useState(
    initialValues?.goal_weight_kg != null
      ? String(displayValueWeight(Number(initialValues.goal_weight_kg)).toFixed(1))
      : ''
  )
  const [notes, setNotes] = useState(initialValues?.notes || '')
  const [error, setError] = useState(null)
  const [prefillTried, setPrefillTried] = useState(false)

  // Pre-fill start weight from latest weight entry — create mode only.
  // Failures are silent; this is a nicety, not a requirement.
  useEffect(() => {
    if (initialValues) { setPrefillTried(true); return undefined }
    if (prefillTried || startWeight) return undefined
    if (!supabaseConfigured || !user) return undefined
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('weight_entries')
        .select('weight')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (!cancelled && data?.weight) {
        setStartWeight(String(displayValueWeight(Number(data.weight)).toFixed(1)))
      }
      if (!cancelled) setPrefillTried(true)
    })()
    return () => { cancelled = true }
  }, [user, initialValues, prefillTried, displayValueWeight, startWeight])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!startWeight || Number(startWeight) <= 0) {
      setError(t('phoenixTracker.phases.form.errorMissingStartWeight'))
      return
    }
    if ((phaseType === 'cut' || phaseType === 'bulk') && (!goalWeight || Number(goalWeight) <= 0)) {
      setError(t('phoenixTracker.phases.form.errorMissingGoalForCutBulk'))
      return
    }
    if (endDate && endDate < startDate) {
      setError(t('phoenixTracker.phases.form.errorEndBeforeStart'))
      return
    }

    const ok = await onSubmit({
      phase_type: phaseType,
      name: name.trim() || null,
      start_date: startDate,
      end_date: endDate || null,
      start_weight_kg: weightToKg(startWeight),
      goal_weight_kg: goalWeight ? weightToKg(goalWeight) : null,
      notes: notes.trim() || null,
    })
    return ok
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-xs text-muted-foreground mb-1.5">
          {t('phoenixTracker.phases.form.phaseType')}
        </label>
        <div className="flex gap-2">
          {TYPES.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPhaseType(p)}
              className={cn(
                'flex-1 h-10 px-3 rounded-lg text-sm font-medium transition-colors',
                phaseType === p
                  ? 'bg-app-primary-500 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              )}
            >
              {t(`phoenixTracker.phases.type.${p}`)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('phoenixTracker.phases.form.name')}
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('phoenixTracker.phases.form.namePlaceholder')}
          className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">
            {t('phoenixTracker.phases.form.startDate')}
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            dir="ltr"
            className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm font-mono"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">
            {t('phoenixTracker.phases.form.endDate')}
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            dir="ltr"
            className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm font-mono"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">
            {t('phoenixTracker.phases.form.startWeight')} ({weightUnit})
          </label>
          <NumberInput
            inputMode="decimal"
            step="0.1"
            value={startWeight}
            onChange={(e) => setStartWeight(e.target.value)}
            placeholder="0.0"
            dir="ltr"
            className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm font-mono"
            required
          />
          {!initialValues && (
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {t('phoenixTracker.phases.form.startWeightHint')}
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">
            {t('phoenixTracker.phases.form.goalWeight')} ({weightUnit})
          </label>
          <NumberInput
            inputMode="decimal"
            step="0.1"
            value={goalWeight}
            onChange={(e) => setGoalWeight(e.target.value)}
            placeholder="0.0"
            dir="ltr"
            className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm font-mono"
          />
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {phaseType === 'maintain'
              ? t('phoenixTracker.phases.form.goalWeightHintMaintain')
              : t('phoenixTracker.phases.form.goalWeightHintCutBulk')}
          </p>
        </div>
      </div>

      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('phoenixTracker.phases.form.notes')}
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('phoenixTracker.phases.form.notesPlaceholder')}
          rows={2}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm resize-none"
        />
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            {t('phoenixTracker.phases.form.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          disabled={submitting}
          className="flex-1 bg-app-primary-500 hover:bg-app-primary-600 text-white"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 animate-spin me-2" /> {t('phoenixTracker.phases.form.saving')}</>
          ) : (
            t('phoenixTracker.phases.form.save')
          )}
        </Button>
      </div>
    </form>
  )
}
